import React from 'react';
import R from 'ramda';
import { connect } from 'react-redux';
import {
  SHOW_CANCEL_SUBMISSION_DIALOG,
  SUBMIT,
} from 'submission/submissionActions';
import { getStep } from 'common/selectors';
import * as steps from 'common/steps';
import { getFields } from 'common/stepReducerUtil';
import StepPanelButtonBar from 'general-components/StepPanelButtonBar';
import styles from './Review.css';

const EMPTY = '---';
const hasValue = (path, fields) => {
  const value = R.path(path, fields);
  return !R.isNil(value) && !R.isEmpty(value);
};

const valueOrEmpty = (path, fields) =>
  hasValue(path, fields) ? R.path(path, fields) : EMPTY;

const nameOrEmpty = R.memoize(
  R.pipe(R.filter(R.identity), R.join(' '), R.when(R.isEmpty, R.always('---'))),
);

const hasFullName = person =>
  !R.isEmpty(person.firstName) && !R.isEmpty(person.lastName);
const hasCommitteeMembers = R.any(hasFullName);

const Review = ({
  contactInfoFields,
  dissertationInfoFields,
  fileUploadFields,
  licenseTypes,
  onCancel,
  onNext,
  onSubmit,
  optionsFields,
}) =>
  <div className={styles.reviewStepPanel}>
    <h1>Review & Submit</h1>

    <div className={styles.row}>
      <div className={styles.column}>
        <h2>Dissertation / Thesis Info</h2>

        <label>Primary language</label>
        <div className={styles.fieldValue}>
          {valueOrEmpty(['language', 'name'], dissertationInfoFields)}
        </div>

        {hasValue(['subtitle'], dissertationInfoFields) &&
          <div>
            <label>Subtitle</label>
            <div className={styles.fieldValue}>
              {dissertationInfoFields.subtitle}
            </div>
          </div>}

        {hasValue(['doi'], dissertationInfoFields) &&
          <div>
            <label>DOI</label>
            <div className={styles.fieldValue}>
              {dissertationInfoFields.doi}
            </div>
          </div>}

        {hasValue(['keywords'], dissertationInfoFields) &&
          <div>
            <label>Keywords</label>
            <div className={styles.fieldValue}>
              {dissertationInfoFields.keywords}
            </div>
          </div>}

        <label>Abstract</label>
        <div className={`${styles.fieldValue} ${styles.noWrap}`}>
          {valueOrEmpty(['abstract'], dissertationInfoFields)}
        </div>

        <h3>License granted to the end user</h3>
        <label>License type</label>
        <div className={styles.fieldValue}>
          {valueOrEmpty(
            ['licenseType', 'licenseTypeName'],
            dissertationInfoFields,
          )}
        </div>

        <h3>Institutional Info</h3>

        <label>Year manuscript completed</label>
        <div className={styles.fieldValue}>
          {valueOrEmpty(['manuscriptCompletionYear'], dissertationInfoFields)}
        </div>

        <label>Institution/University</label>
        <div className={styles.fieldValue}>
          {valueOrEmpty(['institutionName'], dissertationInfoFields)}
        </div>

        <label>Department</label>
        <div className={styles.fieldValue}>
          {valueOrEmpty(
            ['department', 'departmentName'],
            dissertationInfoFields,
          )}
        </div>

        <label>Degree Awarded</label>
        <div className={styles.fieldValue}>
          {valueOrEmpty(
            ['degreeAwarded', 'degreeName'],
            dissertationInfoFields,
          )}
        </div>

        <h3>Categories</h3>
        <label>Primary Category</label>
        <div className={styles.fieldValue}>
          {valueOrEmpty(
            ['primaryCategory', 'categoryName'],
            dissertationInfoFields,
          )}
        </div>

        {hasValue(['secondaryCategory'], dissertationInfoFields) &&
          <div>
            <label>Secondary Category</label>
            <div className={styles.fieldValue}>
              {dissertationInfoFields.secondaryCategory.categoryName}
            </div>
          </div>}

        {hasValue(['tertiaryCategory'], dissertationInfoFields) &&
          <div>
            <label>Tertiary Category</label>
            <div className={styles.fieldValue}>
              {dissertationInfoFields.tertiaryCategory.categoryName}
            </div>
          </div>}

        <h3>Advisors and Committee Members</h3>

        {hasFullName(dissertationInfoFields.advisor) &&
          <div>
            <label>Advisor</label>
            <div className={styles.fieldValue}>
              {nameOrEmpty([
                dissertationInfoFields.advisor.firstName,
                dissertationInfoFields.advisor.lastName,
              ])}
            </div>
          </div>}

        {hasFullName(dissertationInfoFields.supervisor) &&
          <div>
            <label>Supervisor</label>
            <div className={styles.fieldValue}>
              {nameOrEmpty([
                dissertationInfoFields.supervisor.firstName,
                dissertationInfoFields.supervisor.lastName,
              ])}
            </div>
          </div>}

        {hasFullName(dissertationInfoFields.committeeChair) &&
          <div>
            <label>Committee Chair</label>
            <div className={styles.fieldValue}>
              {nameOrEmpty([
                dissertationInfoFields.committeeChair.firstName,
                dissertationInfoFields.committeeChair.lastName,
              ])}
            </div>
          </div>}

        {hasCommitteeMembers(dissertationInfoFields.committeeMembers) &&
          <div>
            <label>Committee Members</label>
            {R.addIndex(R.map)(
              (cm, index) =>
                <div key={`cm-${index}`} className={styles.squatRow}>
                  {nameOrEmpty([cm.firstName, cm.lastName])}
                </div>,
              R.filter(hasFullName, dissertationInfoFields.committeeMembers),
            )}
          </div>}
      </div>

      <div className={styles.column}>
        <h2>Options</h2>

        <label>Delay release</label>
        <div className={styles.fieldValue}>
          {valueOrEmpty(['embargoPeriod'], optionsFields)}
        </div>

        <h2>Contact Info</h2>

        <label>Name</label>
        <div className={styles.fieldValue}>
          {nameOrEmpty([
            contactInfoFields.firstName,
            contactInfoFields.middleName,
            contactInfoFields.lastName,
          ])}
        </div>

        {hasValue(['orcidId'], contactInfoFields) &&
          <div>
            <label>ORCID</label>
            <div className={styles.fieldValue}>
              {contactInfoFields.orcidId}
            </div>
          </div>}

        <label>Institutional student ID</label>
        <div className={styles.fieldValue}>
          {valueOrEmpty(['studentId'], contactInfoFields)}
        </div>

        <label>Email address</label>
        <div className={styles.fieldValue}>
          {valueOrEmpty(['email'], contactInfoFields)}
        </div>

        {hasValue(['phoneNumber'], contactInfoFields) &&
          <div>
            <label>Phone number</label>
            <div className={styles.fieldValue}>
              {contactInfoFields.phoneNumber}
            </div>
          </div>}

        {hasValue(['citizenShip'], contactInfoFields) &&
          <div>
            <label>Country of citizenship</label>
            <div className={styles.fieldValue}>
              {contactInfoFields.citizenShip.name}
            </div>
          </div>}

        <label>Address</label>

        <div className={styles.squatRow}>
          {valueOrEmpty(
            ['permanentAddress', 'addressLineOne'],
            contactInfoFields,
          )}
        </div>

        {hasValue(['permanentAddress', 'addressLineTwo'], contactInfoFields) &&
          <div className={styles.squatRow}>
            {contactInfoFields.permanentAddress.addressLineTwo}
          </div>}

        {hasValue(
          ['permanentAddress', 'addressLineThree'],
          contactInfoFields,
        ) &&
          <div className={styles.squatRow}>
            {contactInfoFields.permanentAddress.addressLineThree}
          </div>}

        <div className={styles.squatRow}>
          {`${valueOrEmpty(
            ['permanentAddress', 'locality'],
            contactInfoFields,
          )}, ${valueOrEmpty(
            ['permanentAddress', 'administrativeArea', 'name'],
            contactInfoFields,
          )} ${valueOrEmpty(
            ['permanentAddress', 'postalCode'],
            contactInfoFields,
          )}`}
        </div>

        {hasValue(['permanentAddress', 'country'], contactInfoFields) &&
          <div>
            <div className={styles.squatRow}>
              {contactInfoFields.permanentAddress.country.name}
            </div>
          </div>}

        <div className={styles.fieldValue} />

        <h2>File Upload</h2>

        <div>
          <label>Dissertation Filename</label>
          <div className={styles.fieldValue}>
            {valueOrEmpty(['dissertationFileName'], fileUploadFields)}
          </div>
        </div>

        {hasValue(['copyrightFileName'], fileUploadFields) &&
          <div>
            <label>Copyright Filename</label>
            <div className={styles.fieldValue}>
              {fileUploadFields.copyrightFileName}
            </div>
          </div>}
      </div>
    </div>

    <StepPanelButtonBar onSubmit={onSubmit} onCancel={onCancel} />
  </div>;

const mapOptionsState = R.pipe(
  getStep(steps.OPTIONS),
  R.applySpec({
    optionsFields: getFields,
  }),
);

const mapContactInfoState = R.pipe(
  getStep(steps.CONTACT_INFO),
  R.applySpec({
    contactInfoFields: getFields,
  }),
);

const mapDissertationInfoState = R.pipe(
  getStep(steps.DISSERTATION_INFO),
  R.applySpec({
    dissertationInfoFields: getFields,
  }),
);

const mapFileUploadState = R.pipe(
  getStep(steps.FILE_UPLOAD),
  R.applySpec({
    fileUploadFields: getFields,
  }),
);

const mapState = R.pipe(
  R.of,
  R.ap([
    mapOptionsState,
    mapContactInfoState,
    mapDissertationInfoState,
    mapFileUploadState,
  ]),
  R.mergeAll,
);

const mapDispatch = dispatch => ({
  onCancel: () => dispatch({ type: SHOW_CANCEL_SUBMISSION_DIALOG, show: true }),
  onSubmit: () => dispatch({ type: SUBMIT }),
});

export default connect(mapState, mapDispatch)(Review);



// WEBPACK FOOTER //
// ./src/review/components/Review.js